import type React from 'react'
import { useState, useMemo } from 'react'
import styled from 'styled-components'

import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import Contact from '@/components/Contact'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import DocumentRequests from '@/components/DocumentRequests'
import AddIncomePanel from '@/components/EntityPanels/AddIncome/AddIncomePanel'
import VerifyIncomePanel from '@/components/EntityPanels/VerifyIncome/Panel'
import ExpandableCard from '@/components/ExpandableCard'
import GuideMe from '@/components/GuideMe'
import H3 from '@/components/H3'
import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import Lightbox, { getFile } from '@/components/Lightbox'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import ReopenSection from '@/components/ReopenSection/ReopenSection'
import { SectionName } from '@/components/ReopenSection/types'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import type { Income as IncomeT, Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { isFinalOutcome } from '@/utils/outcome'
import { isRefCompleted } from '@/utils/subjectHelpers'
import { useUserTracking } from '@/utils/tracking'

import AffordabilityStatement from './AffordabilityStatement'
import Income from './Income'
import { getGuideMeIncomeData } from './utils'

const Body = styled.div`
  display: grid;
  gap: 20px;
`

export const ItemHeader = styled.h2`
  font-family: Montserrat;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  -webkit-letter-spacing: 0.21px;
  -moz-letter-spacing: 0.21px;
  -ms-letter-spacing: 0.21px;
  letter-spacing: 0.21px;
  margin-bottom: 0;

  #document-proof {
    font-weight: normal;
  }
`

export const Action = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  img {
    margin-left: 5px;
  }

  &:hover {
    cursor: pointer;
  }
`

const AddNewIncomeAction = styled(Action)`
  width: 100%;
  max-width: 110px;
`

const IncomeResults = styled.div`
  display: grid;
  padding: 15px;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  grid-gap: 10px;
  background: #f2f2f2;
  border-radius: 10px;

  #header {
    text-align: center;
  }
`

const Checkmark = styled.img`
  width: 10px;
  height: 10px;
`

interface Props {
  incomes: IncomeT[]
  application: Application
  fetchSingleApplication: FetchSingleApplication
  isLoadingTouchpoints: boolean
  subjectTouchpoints: Touchpoint[]
  fetchSubjectTouchpoints: () => Promise<void>
  onApplicantUpdated: () => void
  isRestrictedReference: boolean
  onOpenRequests: () => void
}

// just add the extra results to this array if we expand them in the future
const INCOME_RESULTS = ['incomeStatus']

const IncomeCard: React.FC<Props> = ({
  incomes,
  application,
  fetchSingleApplication,
  isLoadingTouchpoints,
  subjectTouchpoints,
  fetchSubjectTouchpoints,
  onApplicantUpdated,
  isRestrictedReference,
  onOpenRequests,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [verifyingIncome, setVerifyingIncome] = useState(false)
  const [verifyingIncomeIndex, setVerifyingIncomeIndex] = useState<number | null>(null)
  const [addingIncome, setAddingIncome] = useState(false)
  const [incomeIndex, setIncomeIndex] = useState(0)
  const [imageIndex, setImageIndex] = useState(0)

  const { trackUserActivity } = useUserTracking()

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)

  const filteredDocuments = useMemo(() => {
    if (!incomes || incomes.length === 0) return []
    return incomes[incomeIndex].supportingDocuments
  }, [incomeIndex, imageIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const breakdownPoints = useMemo(() => {
    const breakdown: {
      positive: string[]
      negative: string[]
      awaitingSubjectResponse: string[]
      awaitingManualReview: string[]
    } = {
      positive: [],
      negative: [],
      awaitingManualReview: [],
      awaitingSubjectResponse: [],
    }
    if (!application.results) return breakdown

    const financialResults = application.results.find(({ name }) => name === 'financial')

    if (!financialResults || !financialResults.properties) return breakdown

    const incomeResults = financialResults.properties.filter(
      ({ name }) => name && INCOME_RESULTS.includes(name),
    )

    incomeResults.forEach(({ result, decisionInfo }) => {
      if (!decisionInfo) return

      switch (result) {
        case 0:
          breakdown.negative = [...decisionInfo]
          break
        case 1:
          breakdown.positive = [...decisionInfo]
          break
        case 2:
          breakdown.awaitingSubjectResponse = [...decisionInfo]
          break
        case 3:
          breakdown.awaitingManualReview = [...decisionInfo]
          break
      }
    })
    return breakdown
  }, [application])

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'income',
  )

  const handleImageClicked = (incomeIndex: number, imageIndex: number) => {
    const supportingDocument = incomes[incomeIndex].supportingDocuments[imageIndex]
    const isPdf = supportingDocument.thumbnail !== supportingDocument.documentReference
    if (isPdf) {
      window.open(supportingDocument.documentReference, '_blank')
    } else {
      setIncomeIndex(incomeIndex)
      setImageIndex(imageIndex)
      setLightboxOpen(true)

      trackUserActivity('View document', {
        subjectId: application.subjectId,
        customerId: application.customerId,
        fileName: getFile(supportingDocument),
      })
    }
  }

  const handleIncomeVerifyClicked = (incomeIndex: number) => {
    setLightboxOpen(false)
    setVerifyingIncomeIndex(incomeIndex)
    setVerifyingIncome(true)
  }

  const handleAddIncomeClicked = (incomeIndex: number) => {
    setLightboxOpen(false)
    setAddingIncome(true)
  }

  const subjectAssets = application.subjectAssets
    ? application.subjectAssets[0].attributes
    : null

  const applicationAssets = application.assets ? application.assets[0].attributes : null

  const outcomeSections = application?.outcome?.sections ?? []
  const incomeOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.income,
  )
  const overallOutcomeStatus = application.outcome?.status
  const incomeOutcomeStatus = incomeOutcome?.status

  const guideMeData = getGuideMeIncomeData(
    incomeOutcomeStatus,
    application.journeyConfig?.chaseType,
  )

  return (
    <>
      {lightboxOpen && filteredDocuments && filteredDocuments.length > 0 && (
        <Lightbox
          images={filteredDocuments}
          onClose={() => setLightboxOpen(false)}
          customerId={application.customerId}
          subjectId={application.subjectId}
        />
      )}
      <CommentsSidebar
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="INCOME"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />
      {verifyingIncome && verifyingIncomeIndex !== null && incomes && (
        <VerifyIncomePanel
          onSuccess={onApplicantUpdated}
          key={verifyingIncomeIndex}
          income={incomes[verifyingIncomeIndex]}
          setOpen={setVerifyingIncome}
          subjectData={{
            subjectId: application.subjectId,
            applicationId: application.applicationId,
            customerId: application.customerId,
          }}
        />
      )}
      {addingIncome && incomes && (
        <AddIncomePanel
          fetchSingleApplication={fetchSingleApplication}
          setOpen={setAddingIncome}
          subjectData={{
            subjectId: application.subjectId,
            applicationId: application.applicationId,
            customerId: application.customerId,
          }}
          subjectType={application.type}
        />
      )}
      <ExpandableCard
        isExpanded={isExpanded}
        handleExpand={() => setIsExpanded(!isExpanded)}
        label="Income"
        icon="income"
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && incomeOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={incomeOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
        expandedContent={
          <Body>
            <ButtonsContainer>
              <GuideMe steps={guideMeData} />
              <DocumentRequests
                customerId={application.customerId}
                applicationId={application.applicationId}
                subjectId={application.subjectId}
                sectionName={SectionName.income}
                subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
                isRefCompleted={isRefCompleted(application)}
                disabled={isRestrictedReference}
              />
              <Contact
                customerId={application.customerId}
                sectionName="income"
                applicantDetails={{
                  contactName: application.name ?? 'Unknown',
                  subjectId: application.subjectId,
                  phoneNumber: application.context?.mobile ?? 'Unknown',
                }}
                refereeDetails={
                  incomes[0]?.employerReference?.subjectId !== undefined
                    ? {
                        contactName: incomes[0]?.employerReference.name,
                        subjectId: incomes[0]?.employerReference.subjectId,
                        phoneNumber: incomes[0]?.employerReference.phone,
                      }
                    : incomes[0]?.accountantReference?.subjectId !== undefined
                      ? {
                          contactName: incomes[0]?.accountantReference.name,
                          subjectId: incomes[0]?.accountantReference.subjectId,
                          phoneNumber: incomes[0]?.accountantReference.mobile,
                        }
                      : undefined
                }
                agentDetails={{
                  contactName: application?.agent?.name ?? 'Unknown',
                  phoneNumber: application?.agent?.phone ?? 'Unknown',
                }}
                disabled={isRestrictedReference}
              />
              {application.applicationId &&
                application.subjectId &&
                isFinalOutcome(incomeOutcomeStatus) && (
                  <ReopenSection
                    customerId={application.customerId}
                    applicationId={application.applicationId}
                    subjectId={application.subjectId}
                    sectionName={SectionName.income}
                    disabled={isRestrictedReference}
                  />
                )}
            </ButtonsContainer>
            {subjectAssets && applicationAssets && (
              <AffordabilityStatement
                monthlyRentalShare={subjectAssets.priceShare}
                affordabilityRatio={subjectAssets.affordabilityRatio}
                tenancyLength={applicationAssets.term}
                affordabilityThreshold={
                  application.context?.financial?.affordabilityThreshold ?? 0
                }
              />
            )}
            <div>
              <H3>Overview</H3>
              {(breakdownPoints.awaitingManualReview.length > 0 ||
                breakdownPoints.awaitingSubjectResponse.length > 0) && (
                <IncomeResults>
                  <div>
                    <p id="header">👍</p>
                    {[
                      ...breakdownPoints.awaitingManualReview,
                      ...breakdownPoints.awaitingSubjectResponse,
                    ].map((point, i) => (
                      <p key={i}>
                        {' '}
                        <Checkmark src="/assets/svg/info.svg" alt="" /> {point}
                      </p>
                    ))}
                  </div>
                </IncomeResults>
              )}
              {(breakdownPoints.negative.length > 0 ||
                breakdownPoints.positive.length > 0) && (
                <IncomeResults>
                  <div>
                    <p id="header">👍</p>
                    {breakdownPoints &&
                      breakdownPoints.positive.length > 0 &&
                      breakdownPoints.positive.map((point, i) => (
                        <p key={i}>
                          {' '}
                          <Checkmark src="/assets/svg/circle-tick.svg" alt="" /> {point}
                        </p>
                      ))}
                    {breakdownPoints &&
                      breakdownPoints.positive.length === 0 &&
                      'Nothing to show here!'}
                  </div>
                  <div>
                    <p id="header">👎</p>
                    {breakdownPoints.negative.length > 0 &&
                      breakdownPoints.negative.map((point, i) => <p key={i}>{point}</p>)}
                    {breakdownPoints.negative.length === 0 && 'Nothing to show here!'}
                  </div>
                </IncomeResults>
              )}
            </div>
            {incomes.map((income: IncomeT, incomeIndex: number) => (
              <div key={incomeIndex}>
                <hr />
                <Income
                  key={incomeIndex}
                  income={income}
                  incomeIndex={incomeIndex}
                  handleIncomeVerifyClicked={handleIncomeVerifyClicked}
                  application={application}
                  handleImageClicked={handleImageClicked}
                  canRemoveIncome={!isRestrictedReference && incomes.length > 1}
                  subjectId={application.subjectId}
                  customerId={application.customerId}
                  incomeOutcomeStatus={incomeOutcomeStatus}
                  onApplicantUpdated={onApplicantUpdated}
                  hasSingleIncome={incomes.length === 1}
                  isRestrictedReference={isRestrictedReference}
                  onOpenRequests={onOpenRequests}
                />
              </div>
            ))}
            <div>
              <hr />
            </div>
            {!isRestrictedReference && (
              <div className="side-by-side-flex-end">
                <AddNewIncomeAction onClick={() => handleAddIncomeClicked(incomeIndex)}>
                  Add Income{' '}
                  <img
                    src="/assets/svg/add.svg"
                    style={{
                      maxWidth: 25,
                    }}
                    alt=""
                  />
                </AddNewIncomeAction>
              </div>
            )}

            {incomes.length === 0 && breakdownPoints.negative.length > 0 && (
              <div>
                <p>
                  Gurantor is an automatic fail as they have stated they cannot afford the
                  required amount and have elected to not add any incomes.
                </p>
                <p>
                  Another guarantor will need to be added or the requirement of a
                  guarantor to be removed to proceed.
                </p>
              </div>
            )}
          </Body>
        }
      />
    </>
  )
}

export default IncomeCard
